import type { ActiveGame } from '@sea-wars/protocol';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../api/client.js';

/** Odświeżanie listy trwających partii — wystarczająco często, żeby „Twoja kolej” nie kłamała długo. */
const ACTIVE_GAMES_POLL_MS = 15_000;

/**
 * Automatyczne przejście do partii najwyżej raz na załadowanie strony — i tylko jeśli
 * PIERWSZY odczyt zastał gracza na stronie głównej. Późniejsze wejście do lobby (np. z menu
 * w trakcie partii) jest świadomą decyzją i nie może odbijać z powrotem do gry.
 */
let autoResumeChecked = false;

/** Ekran, na którym pasek nie ma sensu: sama partia. */
const isGameScreen = (path: string): boolean => path.startsWith('/game/');

function statusLabel(game: ActiveGame): string {
  if (game.status === 'placement') return 'rozstawianie floty';
  if (game.yourTurn === true) return 'Twoja kolej';
  if (game.yourTurn === false) return 'ruch przeciwnika';
  return 'partia w toku';
}

/**
 * Powrót do trwającej partii z innego urządzenia albo po zamknięciu karty
 * (`docs/08-decyzje.md` #24). Sama gra na kilku urządzeniach naraz działa po stronie serwera
 * (każde gniazdo gracza dostaje te same zdarzenia) — brakowało tylko sposobu, żeby do niej trafić.
 */
export function ActiveGameBanner() {
  const location = useLocation();
  const navigate = useNavigate();
  const [games, setGames] = useState<ActiveGame[]>([]);
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  // biome-ignore lint/correctness/useExhaustiveDependencies: zmiana ekranu (np. powrót z partii do lobby) ma od razu pobrać świeżą listę
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const response = await api.activeGames();
        if (cancelled) return;
        setGames(response.items);
        if (!autoResumeChecked) {
          autoResumeChecked = true;
          const inProgress = response.items.filter((game) => game.status === 'in_progress');
          const only = inProgress[0];
          if (pathRef.current === '/' && inProgress.length === 1 && only) {
            navigate(`/game/${only.id}`);
          }
        }
      } catch {
        // Pojedynczy nieudany odczyt pomijamy — następny cykl albo powrót do karty spróbuje znowu.
      }
    }
    function onVisible() {
      if (document.visibilityState === 'visible') void load();
    }
    void load();
    const timer = window.setInterval(() => void load(), ACTIVE_GAMES_POLL_MS);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [location.pathname, navigate]);

  if (isGameScreen(location.pathname) || games.length === 0) return null;

  return (
    <div className="active-games" role="status">
      {games.map((game) => (
        <p key={game.id} className="active-games__row">
          <span>
            ⚓ Trwa partia
            {game.opponent ? (
              <>
                {' '}
                z <strong>{game.opponent.nickname}</strong>
              </>
            ) : null}{' '}
            — {statusLabel(game)}
          </span>
          <Link to={`/game/${game.id}`} className="btn btn--primary btn--small">
            Wróć do gry
          </Link>
        </p>
      ))}
    </div>
  );
}
